import { useNavigate, useParams } from 'react-router-dom'
import PageDropdown from '../../../components/page-dropdown/PageDropdown.jsx'
import Button from '../../../components/button/Button.jsx'
import { ErrorMessage, Form, Formik } from 'formik'
import * as Yup from 'yup'
import './userManagementForm.scss'
import NewInput from '../../../components/input/NewInput.jsx'
import { ROUTES_PATH, UI_CONSTANT } from '../../../utility/constants.js'
import BreadCrumbs from '../../../components/breadcrumb/Breadcrumb.jsx'
import { useEffect, useState } from 'react'
import {
  generateDropdownOptions,
  handleEmail,
  handleInput,
  handleName,
  handlePassword,
} from '../../../utility/genricFunction.js'
import { useDispatch, useSelector } from 'react-redux'
import { createUser, editUser, getUser } from '../../../store/builders/userManagement/userManagement.builder.js'

import { getRoles } from '../../../store/builders/roleManagement/rolemanagement.builder'
import { PASSWORD_MIN_LENGTH, VALIDATION_MESSAGES } from '../../../utility/constantValidationMessages.js'

const UserManagementForm = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { id } = useParams()
  const { user } = useSelector((store) => store?.userManagement?.userData)
  const { roles } = useSelector((store) => store?.roleManagement?.roleData)
  const branches = useSelector((store) => store?.branch?.branchTableData)
  const [roleOption, setRoleOption] = useState([])
  const branchValue = getValueByLabel(roleOption)

  const [userRoleValue, setUserRoleValue] = useState('')

  const [initialValues, setInitialValues] = useState({
    userName: '',
    userPhone: '',
    userEmail: '',
    userRole: '',
    userPassword: '',
    userBranch: '',
  })

  useEffect(() => {
    dispatch(getRoles())
    if (id) {
      dispatch(getUser(id))
    }
    return () => dispatch({ type: 'user/clearExtraReducers' })
  }, [dispatch, id])

  useEffect(() => {
    const filteredRoles = roles?.filter((role) => {
      const label = role?.roleName?.toLowerCase()
      return label !== 'faculty' && label !== 'student'
    })
    setRoleOption(generateDropdownOptions(filteredRoles, 'roleName', 'roleId'))
  }, [roles])

  useEffect(() => {
    if (id) {
      setInitialValues({
        userName: user?.userName,
        userPhone: user?.userPhone,
        userEmail: user?.userEmail,
        userRole: user?.role_id,
        userBranch: user?.branchId,
        userPassword: '',
      })
    } else {
      dispatch({ type: 'user/clearExtraReducers' })
      setInitialValues({
        userName: '',
        userPhone: '',
        userEmail: '',
        userRole: '',
        userPassword: '',
        userBranch: undefined,
      })
    }
  }, [dispatch, id, user])

  const validationSchema = Yup.object().shape({
    userName: Yup.string().required(VALIDATION_MESSAGES.NAME_REQUIRED),
    userRole: Yup.string().required(VALIDATION_MESSAGES.ROLE),
    userBranch:
      userRoleValue === branchValue
        ? Yup.string().required(VALIDATION_MESSAGES.BRANCH_REQUIRED)
        : Yup.string().nullable().optional(),
    userPhone: Yup.string()
      .required(VALIDATION_MESSAGES.PHONE_NUMBER_REQUIRED)
      .matches(/^[0-9]{10}$/, VALIDATION_MESSAGES.PHONE_VALID),
    userEmail: Yup.string()
      .matches(/\S+@\S+\.\S+/, VALIDATION_MESSAGES.EMAIL_VALID)
      .required(VALIDATION_MESSAGES.EMAIL_REQUIRED),
    userPassword: id
      ? Yup.string().optional()
      : Yup.string()
          .required(VALIDATION_MESSAGES.PASSWORD_REQUIRED)
          .min(PASSWORD_MIN_LENGTH, VALIDATION_MESSAGES.PASSWORD_VALID)
          .matches(
            /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
            VALIDATION_MESSAGES.PASSWORD_VALID,
          ),
  })

  function handleFormSubmit(values) {
    const editObj = {
      userRole: values.userRole,
      userName: values.userName,
      userEmail: values.userEmail,
      userPhone: values.userPhone,
      userPassword: values.userPassword,
      branchId: values.userBranch,
    }
    const createObj = {
      userRole: values.userRole,
      userName: values.userName,
      userEmail: values.userEmail,
      userPhone: values.userPhone,
      userPassword: values.userPassword,
      branchId: values.userBranch,
    }
    if (id) {
      dispatch(editUser({ editObj, id, navigate }))
    } else {
      dispatch(createUser({ data: createObj, navigate }))
    }
  }

  const breadCrumbsArr = [
    {
      title: id ? UI_CONSTANT.USER_MANAGEMENT.EDIT_USER : UI_CONSTANT.USER_MANAGEMENT.ADD_USER,
      link: null,
    },
    {
      title: UI_CONSTANT.DASHBOARD,
      link: ROUTES_PATH.DASHBOARD,
    },
    {
      title: UI_CONSTANT.USER_MANAGEMENT.LABEL_USER_MANAGEMENT,
      link: ROUTES_PATH.USER_MANAGEMENT,
    },
    {
      title: id ? UI_CONSTANT.USER_MANAGEMENT.EDIT_USER : UI_CONSTANT.USER_MANAGEMENT.ADD_USER,
      link: id ? ROUTES_PATH.USER_MANAGEMENT_EDIT.replace(':id', id) : ROUTES_PATH.USER_MANAGEMENT_ADD,
    },
  ]

  function getValueByLabel(array, targetLabel = 'Branch') {
    for (let i = 0; i < array?.length; i++) {
      if (array[i].label.toLowerCase() === targetLabel.toLowerCase()) {
        return array[i].value
      }
    }
    return null
  }

  return (
    <>
      <div className='branchForm-wrapper'>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={(values) => {
            handleFormSubmit(values)
          }}
          enableReinitialize
        >
          {({ values, handleChange, handleSubmit }) => (
            <Form aria-autocomplete='none'>
              <div className='branchForm-header'>
                <BreadCrumbs breadCrumbsArr={breadCrumbsArr} />

                <div className='branchForm-header-right'>
                  <Button customClassName={'save-btn'} onClick={handleSubmit}>
                    {UI_CONSTANT.SAVE}
                  </Button>
                </div>
              </div>
              <div className='branchForm'>
                <div className='branchForm-inner-header'>
                  <h1>{UI_CONSTANT.USER_MANAGEMENT.USER_DETAILS}</h1>
                  <p>{UI_CONSTANT.USER_MANAGEMENT.FORM_DESCRIPTION}</p>
                </div>

                <div className='separatorLine'></div>

                <div className='branchForm-input-fields'>
                  <NewInput
                    label='Name'
                    placeholder='Enter user name'
                    name='userName'
                    value={values?.userName}
                    width={'100%'}
                    required={true}
                    onInput={handleName}
                  />
                  {/* <div className='empty-space'></div> */}

                  <NewInput
                    label='Phone No'
                    placeholder='Enter phone no'
                    value={values?.userPhone}
                    name='userPhone'
                    width={'100%'}
                    type={'text'}
                    required={true}
                    onInput={handleInput}
                    maxLength={10}
                  />
                  <NewInput
                    label='Email'
                    placeholder='Enter email'
                    value={values?.userEmail}
                    name='userEmail'
                    width={'100%'}
                    type={'email'}
                    required={true}
                    maxLength={50}
                    onInput={handleEmail}
                  />

                  <div className='branchForm-input'>
                    <label htmlFor=''>
                      Role <span className='required-symbol'>*</span>
                    </label>

                    <PageDropdown
                      options={roleOption}
                      selectedValue={values.userRole}
                      className={'branchForm-dropdown'}
                      onChange={handleChange}
                      name='userRole'
                      placeholder={'Select role'}
                    />
                    <ErrorMessage name='userRole' component='div' className='error-msg' />
                  </div>
                  {values.userRole === branchValue && (
                    <div className='branchForm-input'>
                      <label htmlFor=''>
                        Branch <span className='required-symbol'>*</span>
                      </label>

                      <PageDropdown
                        options={generateDropdownOptions(branches, 'branchName', 'branchId')}
                        selectedValue={values.userBranch}
                        className={'branchForm-dropdown'}
                        onChange={(e) => {
                          setUserRoleValue(e.target.value)
                          handleChange(e)
                        }}
                        // onChange={handleChange}
                        name='userBranch'
                        placeholder={'Select Branch'}
                      />
                      <ErrorMessage name='userBranch' component='div' className='error-msg' />
                    </div>
                  )}

                  {/* {!id && ( */}
                  <NewInput
                    label='Password'
                    placeholder='Enter user password'
                    name='userPassword'
                    width={'100%'}
                    type={'password'}
                    required={!id}
                    onInput={handlePassword}
                    autoComplete='new-password'
                  />
                  {/* )} */}
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </>
  )
}

export default UserManagementForm
